import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { orderApi, menuApi, tableApi } from '../services/api';
import type { Order, MenuItem, OrderItem, Table, Category } from '../types';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../store';
import { fetchTables } from '../store/slices/tablesSlice';
import axios, { AxiosError } from 'axios';

// Helper function for date formatting
const formatDate = (dateString: string, includeTime: boolean = true) => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    ...(includeTime && {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  };
  return date.toLocaleString(undefined, options);
};

// Components
const TableView: React.FC<{
  data: any[];
  columns: TableColumn[];
  isMobile: boolean;
}> = ({ data, columns, isMobile }) => {
  if (isMobile) {
    return (
      <div className="space-y-4">
        {data.map((item, index) => (
          <div key={index} className="bg-white rounded-lg shadow p-4 space-y-3">
            {columns.map((column, colIndex) => (
              <div key={colIndex} className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-500">{column.header}</span>
                <span className="text-sm text-gray-900">{column.render ? column.render(item) : item[column.key]}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column, index) => (
              <th
                key={index}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((item, index) => (
            <tr key={index}>
              {columns.map((column, colIndex) => (
                <td
                  key={colIndex}
                  className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                >
                  {column.render ? column.render(item) : item[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Modal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}> = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">{title}</h3>
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ActionButton: React.FC<{
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  className?: string;
  children: React.ReactNode;
}> = ({ onClick, variant = 'primary', className = '', children }) => {
  const baseClasses = "px-4 py-2 rounded-md text-white font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 min-h-[44px] min-w-[44px] flex items-center justify-center";
  const variantClasses = {
    primary: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
    secondary: "bg-gray-600 hover:bg-gray-700 focus:ring-gray-500",
    danger: "bg-red-600 hover:bg-red-700 focus:ring-red-500"
  };

  return (
    <button
      onClick={onClick}
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
    >
      {children}
    </button>
  );
};

// Custom hook for mobile detection
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

interface TableColumn {
  header: string;
  key: string;
  render?: (item: any) => React.ReactNode;
}

interface NewOrder {
  table_number: number;
  items: Array<{
    menu_item_id: number;
    quantity: number;
    notes?: string;
  }>;
}

const WaiterDashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('orders');
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [showEditOrder, setShowEditOrder] = useState<number | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState({
    orders: false,
    tables: false,
    menuItems: false,
    categories: false
  });
  const [newOrder, setNewOrder] = useState<{
    table_number: number;
    items: Array<{
      menu_item_id: number;
      quantity: number;
      notes?: string;
    }>;
  }>({
    table_number: 0,
    items: []
  });
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [selectedTable, setSelectedTable] = useState<number | null>(null);

  // Get tables state from Redux
  const { items: tables, loading: tablesLoading } = useSelector((state: RootState) => state.tables);

  // Update useEffect to fetch tables when component mounts
  useEffect(() => {
    dispatch(fetchTables());
    fetchOrders();
    fetchMenuItems();
    fetchCategories();
  }, [dispatch]);

  const fetchOrders = async () => {
    try {
      setIsLoading(prev => ({ ...prev, orders: true }));
      // Fetch all menu items first
      const menuItems = await menuApi.getMenuItems();
      const menuItemsMap = new Map(menuItems.map(item => [item.id, item]));

      const response = await orderApi.getOrders();
      const ordersWithItems = response.map(order => ({
        ...order,
        items: (order.items || []).map(item => ({
          ...item,
          menu_item_name: menuItemsMap.get(item.menu_item_id)?.name || 'Unknown Item',
          price_at_time: menuItemsMap.get(item.menu_item_id)?.price || 0
        }))
      }));
      
      setOrders(ordersWithItems);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to fetch orders');
    } finally {
      setIsLoading(prev => ({ ...prev, orders: false }));
    }
  };

  const fetchMenuItems = async () => {
    setIsLoading(prev => ({ ...prev, menuItems: true }));
    try {
      const response = await menuApi.getMenuItems();
      setMenuItems(response);
    } catch (error) {
      console.error('Error fetching menu items:', error);
      setError('Failed to fetch menu items');
    } finally {
      setIsLoading(prev => ({ ...prev, menuItems: false }));
    }
  };

  const fetchCategories = async () => {
    setIsLoading(prev => ({ ...prev, categories: true }));
    try {
      const response = await menuApi.getCategories();
      setCategories(response);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Failed to fetch categories');
    } finally {
      setIsLoading(prev => ({ ...prev, categories: false }));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleCreateOrder = async () => {
    try {
      if (!newOrder.table_number || newOrder.table_number === 0) {
        setError('Please select a table');
        return;
      }

      if (newOrder.items.length === 0) {
        setError('Please add at least one item');
        return;
      }

      const invalidItems = newOrder.items.filter(item => !item.menu_item_id || item.quantity < 1);
      if (invalidItems.length > 0) {
        setError('Please select valid items and quantities');
        return;
      }

      const totalAmount = newOrder.items.reduce((total, item) => {
        const menuItem = menuItems.find(mi => mi.id === item.menu_item_id);
        return total + (menuItem?.price || 0) * item.quantity;
      }, 0);

      const orderData = {
        table_number: newOrder.table_number,
        items: newOrder.items.map(item => ({
          menu_item_id: item.menu_item_id,
          quantity: item.quantity,
          notes: item.notes || ''  // Ensure notes are included
        })),
        total_amount: totalAmount
      };

      await orderApi.createOrder(orderData);
      setNewOrder({ table_number: 0, items: [] });
      setShowCreateOrder(false);
      setError(null);
      fetchOrders();
      dispatch(fetchTables());
    } catch (error) {
      console.error('Error creating order:', error);
      setError(error instanceof Error ? error.message : 'Failed to create order');
    }
  };

  const renderOrderStatus = (status: Order['status']) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800',
      preparing: 'bg-blue-100 text-blue-800',
      ready: 'bg-green-100 text-green-800',
      served: 'bg-purple-100 text-purple-800',
      cancelled: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const renderTableStatus = (status: 'available' | 'occupied' | 'reserved') => {
    const statusColors = {
      available: 'bg-green-100 text-green-800',
      occupied: 'bg-red-100 text-red-800',
      reserved: 'bg-yellow-100 text-yellow-800'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const handleEditOrder = (order: Order) => {
    if (order.status !== 'pending') {
      setError('Only pending orders can be edited');
      return;
    }
      setEditingOrder(order);
      setNewOrder({
        table_number: order.table_number,
        items: order.items?.map(item => ({
          menu_item_id: item.menu_item_id,
        quantity: item.quantity,
        notes: item.notes
        })) || []
      });
    setShowEditOrder(order.id);
  };

  const handleUpdateOrder = async () => {
    if (!editingOrder || !editingOrder.items) return;

    try {
      // Calculate total amount
      const totalAmount = editingOrder.items.reduce((sum, item) => {
        const menuItem = menuItems.find(mi => mi.id === item.menu_item_id);
        return sum + (menuItem?.price || 0) * item.quantity;
      }, 0);

      // Update the order
      await orderApi.updateOrder(editingOrder.id, {
        table_number: editingOrder.table_number,
        items: editingOrder.items.map(item => ({
          menu_item_id: item.menu_item_id,
          quantity: item.quantity,
          notes: item.notes || ''  // Ensure notes are included
        })),
        total_amount: totalAmount
      });

      // Refresh orders and tables
      await Promise.all([
        fetchOrders(),
        dispatch(fetchTables())
      ]);

      // Close the modal
      setShowEditOrder(null);
      setEditingOrder(null);
    } catch (error) {
      console.error('Error updating order:', error);
      setError('Failed to update order');
    }
  };

  const handleMarkServed = async (orderId: number) => {
    try {
      await orderApi.updateOrderStatus(orderId, 'served');
      fetchOrders();
      dispatch(fetchTables());
    } catch (error) {
      console.error('Error marking order as served:', error);
      setError('Failed to mark order as served');
    }
  };

  const renderOrderActions = (order: Order) => {
    const actions = [];
    
    // Only show edit button for pending orders
    if (order.status === 'pending') {
      actions.push(
        <button
          key="edit"
          onClick={() => handleEditOrder(order)}
          className="text-blue-600 hover:text-blue-800"
        >
          Edit
        </button>
      );
    }

    return actions;
  };

  const getTableStatusColor = (status: string) => {
    switch (status) {
      case 'occupied':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-600'
        };
      case 'reserved':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          text: 'text-yellow-600'
        };
      default:
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          text: 'text-green-600'
        };
    }
  };

  const renderTablesView = () => {
    // Group orders by table
    const tableOrders = tables.map(table => {
      const tableOrders = orders.filter(order => order.table_number === table.number);
      return {
        ...table,
        orders: tableOrders
      };
    });

    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {tableOrders.map((table) => {
          const statusColors = getTableStatusColor(table.status);
          return (
            <div
              key={table.id}
              className={`p-6 rounded-lg shadow-sm text-center cursor-pointer transition-all transform hover:scale-105 ${statusColors.bg} border-2 ${statusColors.border}`}
              onClick={() => handleTableClick(table)}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Table {table.number}</h3>
              <p className={`text-sm font-medium ${statusColors.text}`}>
                {table.status.charAt(0).toUpperCase() + table.status.slice(1)}
              </p>
              {table.orders.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm text-gray-600">
                    {table.orders.length} active {table.orders.length === 1 ? 'order' : 'orders'}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderOrdersView = () => {
    // Group orders by table
    const tableOrders = tables.map(table => {
      const tableOrders = orders.filter(order => order.table_number === table.number);
      return {
        table,
        orders: tableOrders
      };
    });

  return (
      <div className="space-y-6">
        {tableOrders.map(({ table, orders }) => (
          <div key={table.id} className="bg-white rounded-lg shadow p-4 sm:p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                  Table {table.number}
                </h3>
                <p className="text-sm text-gray-600">
                  Status: {table.status}
                </p>
        </div>
              {orders.length > 0 && (
          <button
                  onClick={() => handleNewOrder(table.number)}
                  className="px-3 py-1 sm:px-4 sm:py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                >
                  New Order
          </button>
              )}
            </div>

            {orders.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-gray-500">No orders for this table</p>
          <button
                  onClick={() => handleNewOrder(table.number)}
                  className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create Order
          </button>
        </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.id} className="border-t pt-4 first:border-t-0 first:pt-0">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="text-sm text-gray-600">
                          Order #{order.id} - {formatDate(order.created_at, true)}
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          Status: {order.status}
                        </p>
      </div>
                      {order.status === 'pending' && (
              <button
                          onClick={() => handleEditOrder(order)}
                          className="text-blue-600 hover:text-blue-800"
              >
                          Edit
              </button>
            )}
          </div>
                    <div className="mt-2 space-y-4">
                        {order.items.map((item) => (
                            <div key={item.id} className="border-b pb-4">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">{item.menu_item_name}</h3>
                                        {item.notes && (
                                            <p className="text-sm text-gray-600 mt-1">Note: {item.notes}</p>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm font-medium text-gray-900">{item.menu_item_name}</p>
                                        <p className="text-sm text-gray-500">Quantity: {item.quantity}</p>
                                        <p className="text-sm text-gray-500">Price: ${item.price_at_time.toFixed(2)}</p>
                                        <p className="text-lg font-bold text-blue-600 mt-1">
                                            ${item.price_at_time * item.quantity}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div className="flex justify-between items-center pt-4">
                            <span className="text-lg font-semibold text-gray-900">Total</span>
                            <span className="text-xl font-bold text-blue-600">${order.total_amount.toFixed(2)}</span>
                        </div>
                    </div>
                  </div>
                ))}
              </div>
          )}
        </div>
        ))}
      </div>
    );
  };

  const handleNewOrder = (tableNumber: number) => {
    setNewOrder({
      table_number: tableNumber,
      items: []
    });
    setShowCreateOrder(true);
  };

  const handleTableClick = (table: { id: number; number: number; status: string }) => {
    // Allow clicking on any table, regardless of status
    setSelectedTable(table.number);
    setNewOrder(prev => ({ ...prev, table_number: table.number }));
    setShowCreateOrder(true);
  };

  const handleAddToOrder = (menuItem: MenuItem) => {
    setNewOrder(prev => ({
      ...prev,
      items: [...prev.items, {
        menu_item_id: menuItem.id,
        quantity: 1,
        notes: ''
      }]
    }));
    setShowCreateOrder(true);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Waiter Dashboard</h1>
            <p className="mt-1 text-sm text-gray-600">Manage orders and tables</p>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('orders')}
                className={`${
                  activeTab === 'orders'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Orders
              </button>
              <button
                onClick={() => setActiveTab('tables')}
                className={`${
                  activeTab === 'tables'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Tables
              </button>
              <button
                onClick={() => setActiveTab('menu')}
                className={`${
                  activeTab === 'menu'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Menu
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow">
          {activeTab === 'orders' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Active Orders</h2>
                <button
                  onClick={() => setShowCreateOrder(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  New Order
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {orders.map((order) => (
                  <div key={order.id} className="bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow">
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Order #{order.id}</h3>
                          <p className="text-sm text-gray-500">Table {order.table_number}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          order.status === 'preparing' ? 'bg-blue-100 text-blue-800' :
                          order.status === 'ready' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {order.status}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {order.items?.map((item, index) => (
                          <div key={index} className="flex justify-between items-start">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{item.menu_item_name}</p>
                              <p className="text-sm text-gray-500">x{item.quantity}</p>
                              {item.notes && (
                                <p className="text-sm text-gray-600 mt-1 pl-2 border-l-2 border-gray-200">
                                  Note: {item.notes}
                                </p>
                              )}
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Total:</span>
                              <span className="text-lg font-bold text-blue-600">${item.price_at_time.toFixed(2)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 pt-4 border-t">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-900">Total</span>
                          <span className="text-lg font-semibold text-gray-900">${order.total_amount.toFixed(2)}</span>
                        </div>
                        {order.status === 'ready' && (
                          <button
                            onClick={() => handleMarkServed(order.id)}
                            className="mt-4 w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                          >
                            Mark as Served
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'tables' && (
            <div className="p-6">
              {tablesLoading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                renderTablesView()
              )}
            </div>
          )}

          {activeTab === 'menu' && (
            <div className="space-y-6">
              {/* Category Filters */}
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Categories</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedCategory === null
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    All Items
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        selectedCategory === category.id
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {menuItems
                  .filter(item => selectedCategory === null || item.category_id === selectedCategory)
                  .map((item) => (
                    <div key={item.id} className="bg-white rounded-lg shadow p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">{item.name}</h3>
                          <p className="text-sm text-gray-500">{item.description}</p>
                        </div>
                        <span className="text-lg font-medium text-gray-900">${item.price.toFixed(2)}</span>
                      </div>
                      <div className="mt-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {categories.find(c => c.id === item.category_id)?.name || 'Uncategorized'}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Order Modal */}
      {showCreateOrder && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">New Order</h3>
              <button
                onClick={() => setShowCreateOrder(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Table Number</label>
                <select
                  value={newOrder.table_number}
                  onChange={(e) => setNewOrder({ ...newOrder, table_number: Number(e.target.value) })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value={0}>Select a table</option>
                  {tables.map((table) => (
                    <option key={table.id} value={table.number}>
                      Table {table.number} ({table.status})
                    </option>
                  ))}
                </select>
              </div>

              {/* Category Filters */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Categories</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`px-4 py-2 rounded-full text-sm font-medium ${
                      selectedCategory === null
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    All Items
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium ${
                        selectedCategory === category.id
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Items</label>
                <div className="mt-2 space-y-2">
                  {newOrder.items.map((item, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <select
                        value={item.menu_item_id}
                        onChange={(e) => {
                          const updatedItems = [...newOrder.items];
                          updatedItems[index] = {
                            ...updatedItems[index],
                            menu_item_id: Number(e.target.value)
                          };
                          setNewOrder({ ...newOrder, items: updatedItems });
                        }}
                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value={0}>Select an item</option>
                        {menuItems
                          .filter(item => !selectedCategory || item.category_id === selectedCategory)
                          .map((menuItem) => (
                            <option key={menuItem.id} value={menuItem.id}>
                              {menuItem.name} - ${menuItem.price.toFixed(2)}
                            </option>
                          ))}
                      </select>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => {
                          const updatedItems = [...newOrder.items];
                          updatedItems[index] = {
                            ...updatedItems[index],
                            quantity: Number(e.target.value)
                          };
                          setNewOrder({ ...newOrder, items: updatedItems });
                        }}
                        className="w-20 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        value={item.notes || ''}
                        onChange={(e) => {
                          const updatedItems = [...newOrder.items];
                          updatedItems[index] = {
                            ...updatedItems[index],
                            notes: e.target.value
                          };
                          setNewOrder({ ...newOrder, items: updatedItems });
                        }}
                        placeholder="Notes"
                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                      <button
                        onClick={() => {
                          const updatedItems = newOrder.items.filter((_, i) => i !== index);
                          setNewOrder({ ...newOrder, items: updatedItems });
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setNewOrder({
                    ...newOrder,
                    items: [...newOrder.items, { menu_item_id: 0, quantity: 1 }]
                  })}
                  className="mt-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Add Item
                </button>
              </div>

              {error && (
                <div className="text-red-600 text-sm">{error}</div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateOrder(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateOrder}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Create Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WaiterDashboard; 